import React, { useEffect, useState, useMemo } from "react";
import { formatCurrency } from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import Layout from "../components/Global/Layout";
import { userRequest } from "../api/requestMethods";
import { optimizedImage } from "../utils/cloudinaryImage";
import {
  HoverPreview,
  ImageModal,
  useImagePeek,
} from "../components/ImagePeek";
import {
  cartLineDecreaseAction,
  cartLineIncreaseAction,
  cartLineRemoveAction,
} from "../utils/cartLineSteps";
import {
  setCurrentVendor,
  mergeVendorCarts,
} from "../redux/retailerWholesalerCartSlice";
import {
  setCurrentBrand,
} from "../redux/brandCartSlice";
import { getBrandTheme } from "../utils/brandTheme";
import { lineIdOf } from "../utils/variantCore";
import { StruckPrice, VendorOfferLine } from "../components/PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import {
  cartMinimumVerdict,
  effectiveMinimum,
  linesSubtotal,
  minimumOrderMessage,
} from "../utils/minimumOrder";
import MinimumOrderNotice from "../components/MinimumOrderNotice";
import BranchCartCheck from "../components/branch/BranchCartCheck";
import {
  isQuoteOnly,
  publishedPrice,
  PRICE_ON_REQUEST_LABEL,
  PRICE_UNAVAILABLE_LABEL,
} from "../utils/priceOnRequest";
import CartSummary from "../components/CartSummary";
import useCartQuote from "../hooks/useCartQuote";
import { reorderCartLocation } from "../utils/reorderIntoCart";
import { filledVendorCarts, useCartCount } from "../redux/cartCount";
import DeliveryAreaBar from "../components/DeliveryAreaBar";
import { CartPromiseNote } from "../components/LusukuPromise";

// ─────────────────────────────────────────────────────────────────────────────
// Unified Cart Hub
//
// One cart surface for the whole storefront. Items stay in their existing
// per-vendor-type cart slices (so each vendor's order rules — COD / MOQ / tier
// pricing / delivery — are untouched), but the shopper sees ONE page that
// GROUPS them by vendor: Lusuku in-house, each retailer/wholesaler, each brand.
// Each group has its own subtotal and Checkout button that routes into that
// vendor's existing checkout flow.
//
// ONE CART PER VENDOR, CONCLUDED ONE AT A TIME (Izi, 2 Nov 2026). Every block
// on this page is one vendor's cart and becomes one order; nothing here adds
// carts together. A restaurant or pharmacy cart lives on its storefront (the
// menu, the branch, the hours), so it is listed here as a compact block whose
// action opens that storefront with its own cart open.
//
// Which is also why the minimum order value is gated PER GROUP here, and never
// on the page total: one order per vendor means one rider per vendor, so two
// half-sized vendors are two orders that are each too small to deliver, not one
// that clears. This is the surface where that is easiest to get wrong.
// ─────────────────────────────────────────────────────────────────────────────

const VT = {
  inhouse: { label: "Lusuku", color: "#03660C" },
  retailer: { label: "Retailer / Wholesaler", color: "#2f80ed" },
  brand: { label: "Brand store", color: "#e08a1e" },
  restaurant: { label: "Restaurant", color: "#ef3b47" },
  pharmacy: { label: "Pharmacy", color: "#E91E63" },
};

const Page = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  /* Layout's MainContent already pads the page by the fixed navbar's
     measured height (--lusuku-nav-h), so "Your Cart" starts just under the
     navbar, as the homepage banner does. Offsetting by the navbar again here
     left a gap of a whole navbar above the heading. */
  padding: 16px 16px 70px;
`;
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  h1 { font-size: 22px; margin: 0; color: #1f2a2e; display: flex; align-items: center; gap: 10px; }
  .count { font-size: 13px; color: #6b7a80; font-weight: 600; }
`;
const Continue = styled.button`
  border: 1px solid #03660c; background: #fff; color: #03660c;
  border-radius: 999px; padding: 9px 18px; font-size: 13px; font-weight: 700; cursor: pointer;
  &:hover { background: #f3f9ef; }
`;
const Group = styled.div`
  background: #fff;
  border: 1px solid #eef1f2;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.06);
  margin-bottom: 18px;
  overflow: hidden;
`;
const GroupHead = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 13px 16px;
  border-bottom: 1px solid #f0f3f4;
  background: #fafcfb;
  .dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; background: ${(p) => p.$color}; }
  .nm { font-weight: 800; font-size: 14.5px; color: #25323a; }
  .tag { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: var(--brand-primary, #6b7a80); background: var(--brand-secondary-soft, #eef2f3); padding: 2px 8px; border-radius: 999px; }
  .n { margin-left: auto; font-size: 12.5px; color: #6b7a80; }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #f4f6f7;
  &:last-child { border-bottom: 0; }
  @media (max-width: 560px) { flex-wrap: wrap; }
`;
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
  @media (max-width: 560px) {
    width: 100%;
    margin-left: 0;
    margin-top: 4px;
    padding-left: 68px; /* align under the product info (thumb 56 + gap 12) */
    justify-content: space-between;
  }
`;
const Thumb = styled.div`
  width: 56px; height: 56px; flex-shrink: 0;
  border: 1px solid #eef1f2; border-radius: 9px; background: #fff;
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; padding: 4px; }

  /* A button, not a bare box: hover AND keyboard focus open the preview, and
     Enter opens the full picture. Invisible as a control — the thumbnail is
     the affordance, the cursor confirms it. */
  button {
    width: 100%; height: 100%;
    padding: 0; border: 0; background: transparent;
    display: flex; align-items: center; justify-content: center;
    cursor: zoom-in;
  }
  button:focus-visible {
    outline: 3px solid #7cb342;
    outline-offset: 1px;
    border-radius: 9px;
  }
`;
const Info = styled.div`
  flex: 1; min-width: 0;
  .nm { font-size: 13.5px; font-weight: 600; color: #2c3a40; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .pr { font-size: 12.5px; color: #6b7a80; margin-top: 2px; }
`;
const Qty = styled.div`
  display: flex; align-items: center; gap: 0; border: 1px solid #dfe5e7; border-radius: 8px; overflow: hidden;
  button { width: 30px; height: 30px; border: 0; background: #fff; cursor: pointer; font-size: 16px; color: #33454b; }
  button:hover { background: var(--brand-secondary-soft, #f1f5f3); }
  span { min-width: 30px; text-align: center; font-size: 13px; font-weight: 700; color: #25323a; }
`;
const LineTotal = styled.div`
  width: 110px; text-align: right; font-size: 14px; font-weight: 800; color: var(--brand-primary, #0f9aa8);
  @media (max-width: 560px) { width: 80px; font-size: 12.5px; }
`;
const Remove = styled.button`
  border: 0; background: transparent; color: #c0392b; cursor: pointer; font-size: 12px; font-weight: 700; padding: 4px 6px;
  &:hover { text-decoration: underline; }
`;
const GroupFoot = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 16px;
  padding: 13px 16px; background: #fafcfb; border-top: 1px solid #f0f3f4; flex-wrap: wrap;
  @media (max-width: 560px) {
    justify-content: space-between;
  }
`;
/* The growth-event vendor type for each hub group. */
const HUB_GROUP_VENDOR_TYPE = {
  inhouse: "lusuku",
  retailer: "shop",
  brand: "brand",
  restaurant: "restaurant",
  pharmacy: "pharmacy",
};

/* A storefront cart's block: its money in one line, and the way to its cart. */
const StorefrontFoot = styled.div`
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  padding: 10px 16px 13px;
  > :first-child { flex: 1; min-width: 200px; margin: 0; }
  @media (max-width: 560px) { > * { width: 100%; } }
`;
const OfferNote = styled.p`
  margin: 0 0 14px; padding: 10px 14px; border-radius: 10px;
  background: #eef7f1; border: 1px solid #cfe6d8; color: #0a5c40;
  font-size: 13.5px; font-weight: 600; line-height: 1.4;
`;

const Checkout = styled.button`
  border: 0; background: var(--brand-secondary, #03660c); color: var(--brand-on-secondary, #fff); border-radius: 9px;
  padding: 11px 22px; font-size: 13.5px; font-weight: 800; cursor: pointer;
  &:hover { background: var(--brand-secondary-hover, #02550a); }
  /* A basket too small to deliver: still legible, plainly not pressable. */
  &:disabled { background: #cdd7d9; color: #5b6b70; cursor: not-allowed; }
  &:disabled:hover { background: #cdd7d9; }
  @media (max-width: 560px) { width: 100%; }
`;
const Empty = styled.div`
  text-align: center; padding: 70px 16px; color: #6b7a80;
  h3 { color: #33454b; margin: 14px 0 6px; }
`;

// Stable empty references so selector fallbacks don't churn memo/effect deps.
const EMPTY_ARR = [];
const EMPTY_OBJ = {};

const imgOf = (it) => it.image || it.imageUrl || it.product_image || "";

/**
 * What one unit of this line costs, or null when nothing may be charged for it.
 *
 * This used to be `Number(it.price || 0) + Number(it.specPriceDelta || 0)`, and
 * this page is where shoppers actually land: both the brand storefront and the
 * brand product page push to /cart. A price-on-request line therefore rendered
 * "USh 0 each" with a "USh 0" line total, and, worse, contributed 0 to the
 * group subtotal the minimum-order check reads, so a cart holding nothing but
 * quote-only items cleared the floor (a shortfall against a subtotal of 0 is
 * reported as no shortfall) and lit up a live Checkout button for goods with no
 * prices. Returning null makes both of those impossible: the row has no number
 * to print and the subtotal has nothing to add.
 */
const unitPriceOf = (it) => {
  const base = publishedPrice(it);
  if (base === null) return null;
  return base + (Number(it.specPriceDelta) || 0);
};

/** The old name, kept for the callers that only ever want a number to add up. */
const priceOf = (it) => unitPriceOf(it) ?? 0;

/** Why this line cannot be checked out, or null when it can. */
const lineProblem = (it) => {
  if (isQuoteOnly(it)) return PRICE_ON_REQUEST_LABEL;
  if (publishedPrice(it) === null) return PRICE_UNAVAILABLE_LABEL;
  return null;
};
// The list price the vendor's offer cut from, for the strike-through.
const listPriceOf = (it) => Number(it.listPrice || 0) + Number(it.specPriceDelta || 0);
const qtyOf = (it) => Number(it.qty ?? it.quantity ?? 1);

const CartHub = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const inHouse = useSelector((s) => s.cart.products) ?? EMPTY_ARR;
  const rwCarts = useSelector((s) => s.retailerWholesalerCart?.carts) ?? EMPTY_OBJ;
  const brandCarts = useSelector((s) => s.brandCart?.carts) ?? EMPTY_OBJ;
  const restaurantCarts = useSelector((s) => s.restaurantCart?.carts) ?? EMPTY_OBJ;
  const pharmacyCarts = useSelector((s) => s.healthcareCart?.carts) ?? EMPTY_OBJ;
  /* The storefront each was last opened on: a name to show until the
     server's answer names the vendor. */
  const currentRestaurant = useSelector((s) => s.restaurantCart?.currentRestaurant);
  const restaurantShown = useSelector((s) => s.restaurantCart?.restaurant);
  const currentPharmacy = useSelector((s) => s.healthcareCart?.currentHealthcareVendor);
  const pharmacyShown = useSelector((s) => s.healthcareCart?.vendor);

  /* One hover preview and one modal for the whole cart, however many lines it
     has. See components/ImagePeek.jsx. */
  const peek = useImagePeek();

  const [vendorNames, setVendorNames] = useState({});
  const [brandThemes, setBrandThemes] = useState({});
  /* A brand may set its own, higher, minimum order value. It rides along on the
     brand lookup below, so the hub asks the same question of the same document
     the brand cart does and the two cannot disagree. */
  const [brandFloors, setBrandFloors] = useState({});

  // Resolve current store / brand names for the group headers.
  // SELF-HEAL while we're at it: the backend RW-vendor lookup matches
  // `{$or:[{_id},{owner}]}`, so a persisted cart key can be the OWNER (Users)
  // id alias instead of the canonical store `_id` — which renders ONE vendor
  // as TWO groups (two checkouts, double delivery fee). The lookup response
  // carries the canonical `_id`; when it differs from the cart key, merge the
  // alias cart into the canonical one. Per-vendor cart segregation is
  // untouched — this only fixes the key identity.
  useEffect(() => {
    const rwIds = Object.keys(rwCarts).filter(
      (id) => Object.keys(rwCarts[id] || {}).length
    );
    rwIds.forEach((id) => {
      if (vendorNames[`rw:${id}`]) return;
      userRequest(`/rw/rw-vendors/${id}`)
        .then((r) => {
          const d = r.data;
          const entity = d?.vendor || d?.[0] || d;
          const name =
            d?.name || d?.tradingName || d?.[0]?.name || d?.vendor?.name;
          const canonicalId = entity?._id ? String(entity._id) : null;
          if (canonicalId && canonicalId !== String(id)) {
            dispatch(mergeVendorCarts({ fromId: id, toId: canonicalId }));
            if (name)
              setVendorNames((p) => ({ ...p, [`rw:${canonicalId}`]: name }));
            return;
          }
          if (name) setVendorNames((p) => ({ ...p, [`rw:${id}`]: name }));
        })
        .catch(() => {});
    });
    const bIds = Object.keys(brandCarts).filter(
      (id) => Object.keys(brandCarts[id] || {}).length
    );
    bIds.forEach((id) => {
      if (vendorNames[`brand:${id}`] && brandThemes[id]) return;
      userRequest(`/api/brands/${id}`)
        .then((r) => {
          const entity = r.data?.data || r.data?.brand || r.data;
          const name = entity?.tradingName || entity?.name;
          if (name) setVendorNames((p) => ({ ...p, [`brand:${id}`]: name }));
          if (entity) {
            setBrandThemes((previous) => ({
              ...previous,
              [id]: getBrandTheme(entity),
            }));
            setBrandFloors((previous) => ({
              ...previous,
              [id]: Number(entity.fulfilment?.minimumOrderValueUgx) || 0,
            }));
          }
        })
        .catch(() => {});
    });
    // eslint-disable-next-line
  }, [rwCarts, brandCarts]);

  const minimumOrderValue = useMinimumOrderValue();
  const groups = useMemo(() => {
    const g = [];
    if (inHouse.length) {
      g.push({ kind: "inhouse", id: "lusuku", name: VT.inhouse.label, color: VT.inhouse.color, items: inHouse });
    }
    filledVendorCarts(rwCarts).forEach(({ key: vendorId, items }) => {
      g.push({
        kind: "retailer", id: vendorId,
        name: vendorNames[`rw:${vendorId}`] || VT.retailer.label,
        color: VT.retailer.color, items,
      });
    });
    filledVendorCarts(brandCarts).forEach(({ key: brandId, items }) => {
      const theme = brandThemes[brandId] || getBrandTheme();
      g.push({
        kind: "brand", id: brandId,
        name: vendorNames[`brand:${brandId}`] || VT.brand.label,
        color: theme.primary, theme, items,
        minimum: effectiveMinimum(minimumOrderValue, brandFloors[brandId]),
      });
    });
    /* The subtotal each group's own row prints, computed once and reused by the
       minimum-order check, so the notice can never quote a different number
       from the one right beside it. */
    return g.map((group) => {
      /* Lines that cannot be charged are excluded from the subtotal AND
         counted separately, because the two facts drive different messages.
         Mixing them was the bug: a quote-only line valued at zero dragged a
         mixed cart under the vendor's floor, so the shopper was told to add
         more money to an order that was actually blocked for a different
         reason entirely. */
      const unsellable = group.items.filter((it) => lineProblem(it) !== null);
      return {
        ...group,
        unsellable,
        subtotal: group.items
          .filter((it) => lineProblem(it) === null)
          .reduce((s, it) => s + priceOf(it) * qtyOf(it), 0),
        minimum: group.minimum ?? minimumOrderValue,
      };
    });
  }, [inHouse, rwCarts, brandCarts, vendorNames, brandThemes, brandFloors, minimumOrderValue]);

  /* Restaurant and pharmacy carts: reviewed and checked out on their own
     storefront, listed here so no cart is out of sight. */
  const storefronts = useMemo(
    () => [
      ...filledVendorCarts(restaurantCarts).map(({ key, items }) => ({
        kind: "restaurant",
        id: key,
        items,
        shownName: String(key) === String(currentRestaurant) ? restaurantShown?.name : null,
      })),
      ...filledVendorCarts(pharmacyCarts).map(({ key, items }) => ({
        kind: "pharmacy",
        id: key,
        items,
        shownName: String(key) === String(currentPharmacy) ? pharmacyShown?.name : null,
      })),
    ].map((block) => ({
      ...block,
      outletId: block.items.find((line) => line?.outletId)?.outletId || null,
    })),
    [restaurantCarts, pharmacyCarts, currentRestaurant, restaurantShown, currentPharmacy, pharmacyShown]
  );

  const cartCount = groups.length + storefronts.length;
  // The same count as the header badge (redux/cartCount).
  const totalItems = useCartCount();

  /* Every cart on the page quoted by the server in ONE call (POST
     /api/quote/cart), each on its own: its delivery fee, goods subtotal,
     total and minimum-order verdict. Each cart is its own order, so each
     shows its FULL delivery fee; the shopper's free delivery goes to the
     first cart checked out, and the server says so once (`offerNote`). A
     group holding a line that cannot be charged is not quoted: it shows no
     estimate and cannot check out until the line is gone. */
  const quoteGroups = useMemo(
    () => [
      ...groups
        .filter((group) => !group.unsellable?.length)
        .map((group) => ({
          key: `${group.kind}:${group.id}`,
          vendorId: group.kind === "inhouse" ? group.items[0]?.vendorId : group.id,
          outletId: group.items.find((line) => line?.outletId)?.outletId || null,
          items: group.items,
        })),
      ...storefronts.map((block) => ({
        key: `${block.kind}:${block.id}`,
        vendorId: block.id,
        outletId: block.outletId,
        items: block.items,
      })),
    ],
    [groups, storefronts]
  );
  const cartQuote = useCartQuote({ groups: quoteGroups });

  /* A storefront's cart opens on the storefront itself, the way "Order
     again" lands there (utils/reorderIntoCart). */
  const openStorefrontCart = (block, name) =>
    history.push(
      reorderCartLocation({ vertical: block.kind, vendorId: block.id, vendorName: name, outletId: block.outletId })
    );

  /* Per-vendor minimum-order verdicts, keyed the same way the group rows are,
     so each group's footer reads its own and no group is judged on another's
     items: the server's, from the cart quote, with the group's own sum
     standing in only until that answer arrives. */
  const minimumByKey = groups.reduce((map, group) => {
    const key = `${group.kind}:${group.id}`;
    map[key] = cartMinimumVerdict(cartQuote.estimateOf(key), {
      subtotal: group.subtotal,
      minimum: group.minimum,
    });
    return map;
  }, {});
  const shortfallOf = (group) => minimumByKey[`${group.kind}:${group.id}`]?.shortfall || 0;

  /* The same steps a /shop grid card's quantity control dispatches
     (utils/cartLineSteps.js). */
  const inc = (g, it) => dispatch(cartLineIncreaseAction(g.kind, g.id, it));
  const dec = (g, it) => dispatch(cartLineDecreaseAction(g.kind, g.id, it));
  const remove = (g, it) => dispatch(cartLineRemoveAction(g.kind, g.id, it));
  const checkout = (g) => {
    // The button is already disabled for a short group; this catches anything
    // that reaches the handler another way (a keyboard path, a stale render).
    if (shortfallOf(g) > 0) return;
    /* Same belt-and-braces refusal for a group holding a line that cannot be
       charged. The server refuses these lines outright (PRICE_ON_REQUEST or
       PRICE_UNSET), so letting the shopper reach a payment screen with one in
       the basket only moves the refusal somewhere it reads as a payment
       failure. */
    if (g.unsellable?.length) return;
    if (g.kind === "inhouse") history.push("/shipping");
    else if (g.kind === "retailer") { dispatch(setCurrentVendor(g.id)); history.push("/retailer-wholesaler-shipping"); }
    else { dispatch(setCurrentBrand(g.id)); history.push("/brand-shipping"); }
  };

  return (
    <Layout>
      <Page>
        <TopBar>
          <h1>
            Your Cart
            {totalItems > 0 && <span className="count">{totalItems} item{totalItems !== 1 ? "s" : ""} · {cartCount} {cartCount === 1 ? "cart" : "carts"}</span>}
          </h1>
          <Continue onClick={() => history.push("/shop")}>Continue shopping</Continue>
        </TopBar>

        {cartCount > 0 && <DeliveryAreaBar />}
        {cartCount > 0 && cartQuote.offerNote && <OfferNote role="note">{cartQuote.offerNote}</OfferNote>}

        {cartCount === 0 ? (
          <Empty>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#c4ced2" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            <h3>Your cart is empty</h3>
            <p>Browse the shop and add items. Each vendor’s items appear here as their own cart.</p>
            <Continue onClick={() => history.push("/shop")} style={{ marginTop: 12 }}>Start shopping</Continue>
          </Empty>
        ) : (
          groups.map((g) => {
            const subtotal = g.subtotal;
            const offerSavings = cartOfferSavings(g.items);
            const groupKey = `${g.kind}:${g.id}`;
            const minimum = minimumByKey[groupKey];
            const shortfall = shortfallOf(g);
            return (
              <Group
                key={`${g.kind}:${g.id}`}
                style={g.theme ? {
                  "--brand-primary": g.theme.primary,
                  "--brand-secondary": g.theme.secondary,
                  "--brand-on-primary": g.theme.onPrimary,
                  "--brand-on-secondary": g.theme.onSecondary,
                  "--brand-primary-hover": g.theme.primaryHover,
                  "--brand-secondary-hover": g.theme.secondaryHover,
                  "--brand-secondary-soft": g.theme.secondarySoft,
                } : undefined}
              >
                {/* A brand cart is checked at its branch when the hub opens
                    (4 Oct 2026): lines read back from localStorage, moved or
                    touched by a live branch change show "Changes at
                    <branch>" here rather than at the payment step. */}
                {g.kind === "brand" && <BranchCartCheck vertical="brand" cartId={g.id} />}
                <GroupHead $color={g.color}>
                  <span className="dot" />
                  <span className="nm">{g.name}</span>
                  <span className="tag">{VT[g.kind].label}</span>
                  <span className="n">{g.items.length} item{g.items.length !== 1 ? "s" : ""}</span>
                </GroupHead>

                {g.items.map((it) => (
                  <Row key={g.kind === "brand" ? lineIdOf(it) : it._id}>
                    <Thumb>
                      {imgOf(it) ? (
                        <button
                          type="button"
                          aria-label={`View ${it.name || it.title || "product"} larger`}
                          title="Click to view larger"
                          {...peek.handlers(imgOf(it), it.name || it.title)}
                        >
                          <img
                            src={optimizedImage(imgOf(it), "thumb")}
                            alt={it.name || it.title}
                            loading="lazy"
                            onError={(e) => {
                              // Broken/legacy image URL — drop to the neutral
                              // box rather than showing a broken-image glyph.
                              e.currentTarget.onerror = null;
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        </button>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c4ced2" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
                        </svg>
                      )}
                    </Thumb>
                    <Info>
                      <div className="nm">{it.name || it.title}</div>
                      {Object.keys(it.selectedSpecs || {}).length > 0 && (
                        <div className="pr">
                          {Object.entries(it.selectedSpecs).map(([key, value]) =>
                            `${it.specOptions?.find((option) => option.fieldKey === key)?.label || key}: ${value}`
                          ).join(" · ")}
                        </div>
                      )}
                      <div className="pr">
                        {lineProblem(it) ? (
                          lineProblem(it)
                        ) : (
                          <>
                            {lineHasDeal(it) && (
                              <StruckPrice>{formatCurrency(listPriceOf(it))}</StruckPrice>
                            )}
                            {formatCurrency(priceOf(it))} each
                          </>
                        )}
                      </div>
                    </Info>
                    <Controls>
                      <Qty>
                        <button onClick={() => dec(g, it)} aria-label="Decrease">−</button>
                        <span>{qtyOf(it)}</span>
                        <button onClick={() => inc(g, it)} aria-label="Increase">+</button>
                      </Qty>
                      <LineTotal>
                        {lineProblem(it) ? "" : formatCurrency(priceOf(it) * qtyOf(it))}
                      </LineTotal>
                      <Remove onClick={() => remove(g, it)}>Remove</Remove>
                    </Controls>
                  </Row>
                ))}

                <GroupFoot>
                  <MinimumOrderNotice
                    shortfall={g.unsellable?.length ? null : minimum.shortfall}
                    minimum={minimum.minimum}
                    subtotal={g.unsellable?.length ? null : minimum.subtotal}
                    vendorType={HUB_GROUP_VENDOR_TYPE[g.kind]}
                    perVendor={cartCount > 1}
                    style={{ width: "100%", margin: 0 }}
                  />
                  <VendorOfferLine
                    amount={offerSavings}
                    format={formatCurrency}
                    style={{ padding: 0, fontSize: "13.5px" }}
                  />
                  {/* A group holding a line that cannot be charged is not
                      quoted, so it shows the subtotal of what can be. */}
                  <CartSummary
                    estimate={g.unsellable?.length ? null : cartQuote.estimateOf(groupKey)}
                    subtotal={subtotal}
                    vendorId={g.kind === "inhouse" ? g.items[0]?.vendorId : g.id}
                    vendorType={HUB_GROUP_VENDOR_TYPE[g.kind]}
                    style={{ width: "100%" }}
                  />
                  {/* The quote-only blocker is tested BEFORE the minimum-order
                      one, because it is the more specific truth: telling a
                      shopper to spend more on a basket whose items have no
                      prices explains nothing and cannot be acted on. */}
                  <Checkout
                    onClick={() => checkout(g)}
                    disabled={shortfall > 0 || Boolean(g.unsellable?.length)}
                  >
                    {g.unsellable?.length
                      ? g.unsellable.length === 1
                        ? "One item needs a quote first"
                        : `${g.unsellable.length} items need a quote first`
                      : shortfall > 0
                        ? minimumOrderMessage(shortfall, formatCurrency)
                        : `Checkout ${g.kind === "inhouse" ? "Lusuku" : g.name} order`}
                  </Checkout>
                </GroupFoot>
              </Group>
            );
          })
        )}
        {storefronts.map((block) => {
          const blockKey = `${block.kind}:${block.id}`;
          const estimate = cartQuote.estimateOf(blockKey);
          const name = estimate.vendorName || block.shownName || VT[block.kind].label;
          return (
            <Group key={blockKey} data-cart={blockKey}>
              <GroupHead $color={VT[block.kind].color}>
                <span className="dot" />
                <span className="nm">{name}</span>
                <span className="tag">{VT[block.kind].label}</span>
                <span className="n">{block.items.length} item{block.items.length !== 1 ? "s" : ""}</span>
              </GroupHead>
              <StorefrontFoot>
                <CartSummary
                  estimate={estimate}
                  subtotal={linesSubtotal(block.items)}
                  vendorId={block.id}
                  vendorType={HUB_GROUP_VENDOR_TYPE[block.kind]}
                  compact
                />
                <Checkout type="button" onClick={() => openStorefrontCart(block, name)}>
                  Open cart
                </Checkout>
              </StorefrontFoot>
            </Group>
          );
        })}
        {cartCount > 0 && <CartPromiseNote />}
      </Page>

      <HoverPreview
        src={peek.hover?.src}
        rect={peek.hover?.rect}
        alt={peek.hover?.alt || ""}
      />
      <ImageModal
        src={peek.modal?.src}
        alt={peek.modal?.alt}
        onClose={peek.closeModal}
      />
    </Layout>
  );
};

export default CartHub;
